import React, { useState, useEffect, useCallback, useMemo, createContext, useContext } from 'react';

type ThemeMode = 'light' | 'dark' | 'system';

interface ThemeContextType {
  mode: ThemeMode;
  setMode: (mode: ThemeMode) => void;
  isDark: boolean;
}

export const ThemeContext = createContext<ThemeContextType>({
  mode: 'light',
  setMode: () => { },
  isDark: false,
});

export const useThemeMode = () => useContext(ThemeContext);

const getSystemPreference = (): boolean => {
  if (typeof window !== 'undefined') {
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  }
  return false;
};

const ThemeProviderWrapper: React.FC<React.PropsWithChildren<{}>> = ({ children }) => {
  const [mode, setModeState] = useState<ThemeMode>(() => {
    const saved = localStorage.getItem('themeMode') as ThemeMode | null;
    return saved || 'light';
  });

  const [systemPreference, setSystemPreference] = useState(getSystemPreference());

  const isDark = useMemo(() => {
    if (mode === 'system') return systemPreference;
    return mode === 'dark';
  }, [mode, systemPreference]);

  const setMode = useCallback((newMode: ThemeMode) => {
    localStorage.setItem('themeMode', newMode);
    setModeState(newMode);
  }, []);

  // Sync dark class on root element for Tailwind — disable transitions for instant theme switch
  useEffect(() => {
    const root = document.documentElement;

    // Temporarily disable all transitions so theme switch is instant
    root.classList.add('disable-transitions');

    if (isDark) {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }

    // Re-enable transitions after a single frame
    const raf = requestAnimationFrame(() => {
      // Wait one more frame to ensure styles have been applied
      requestAnimationFrame(() => {
        root.classList.remove('disable-transitions');
      });
    });

    return () => cancelAnimationFrame(raf);
  }, [isDark]);

  // Listen for system preference changes globally
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e: MediaQueryListEvent) => setSystemPreference(e.matches);
    mediaQuery.addEventListener('change', handler);
    return () => mediaQuery.removeEventListener('change', handler);
  }, []);

  return (
    <ThemeContext.Provider value={{ mode, setMode, isDark }}>
      {children}
    </ThemeContext.Provider>
  );
};

export default ThemeProviderWrapper;
